import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { 
  ExternalLink,
  RefreshCw,
  FileText,
  Edit,
  Trash2,
  Eye,
  Receipt
} from "lucide-react";
import { format } from "date-fns";
import { InvoiceStatusBadge } from './InvoiceStatusBadge';
import { InvoiceDueBadge } from './InvoiceDueBadge';
import { SortableHeader } from '@/components/ui/sortable-header';


interface Invoice {
  id: string;
  title?: string;
  client_id: string;
  project_id?: string;
  amount: number;
  currency?: string;
  issue_date?: string;
  due_date?: string;
  paid_date?: string;
  status?: string;
  description?: string;
  created_at: string;
  updated_at: string;
  invoice_number?: string;
  po_number?: string;
  po_type?: string;
  discount_percentage?: number;
  pdf_url?: string;
  clients?: { name: string; id: string };
  projects?: { name: string; id: string };
  invoice_items?: Array<{
    id: string;
    product_id: string;
    quantity: number;
    unit_price: number;
    products?: { id: string; name: string; base_price?: number };
  }>;
}

interface InvoiceTableProps {
  invoices: Invoice[];
  isLoading: boolean;
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  onNextPage: () => void;
  onPrevPage: () => void;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  onEdit?: (invoice: Invoice) => void;
  onDelete?: (invoice: Invoice) => void;
  onView?: (invoice: Invoice) => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
}

// Helper function to format currency
const formatCurrency = (amount: number, currency: string) => {
  const locales: Record<string, string> = {
    'EUR': 'el-GR',
    'USD': 'en-US', 
    'RON': 'ro-RO',
    'PLN': 'pl-PL'
  };
  
  return new Intl.NumberFormat(locales[currency] || 'en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

export function InvoiceTable({ 
  invoices, 
  isLoading, 
  currentPage, 
  totalItems, 
  itemsPerPage, 
  onNextPage, 
  onPrevPage, 
  hasNextPage, 
  hasPrevPage,
  onEdit,
  onDelete,
  onView,
  sortBy,
  sortDirection,
  onSort
}: InvoiceTableProps) {
  const [deleteInvoice, setDeleteInvoice] = useState<Invoice | null>(null);
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  if (!isLoading && invoices.length === 0) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No invoices found</h3>
            <p className="text-muted-foreground">Try adjusting your filters or search term, or create your first invoice.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <SortableHeader
                    title="Invoice Number"
                    isSorted={sortBy === 'invoice_number' ? (sortDirection === 'asc' ? 'asc' : 'desc') : false}
                    onSort={() => onSort?.('invoice_number')}
                  />
                </TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Project</TableHead>
                <TableHead>Products</TableHead>
                <TableHead>PO Number</TableHead>
                <TableHead>Issue Date</TableHead>
                <TableHead>Due Date</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Paid Date</TableHead>
                <TableHead>PDF</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-6 w-[70px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[50px]" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-8" /></TableCell>
                  </TableRow>
                ))
              ) : invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center py-8">
                      <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-medium text-foreground mb-2">No invoices found</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Get started by creating your first invoice
                      </p>
                      <Button onClick={() => window.location.href = '/invoices'}>
                        Create Invoice
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => {
                  return (
                    <TableRow key={invoice.id} className="cursor-pointer hover:bg-muted/50">
                      <TableCell className="font-mono text-sm">
                        {invoice.invoice_number || "—"}
                      </TableCell>
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-2">
                          <span>{invoice.title || `Invoice #${invoice.id.slice(-8)}`}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          <span className="hover:text-primary cursor-pointer">
                            {invoice.clients?.name || "Unknown Client"}
                          </span>
                          <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        </div>
                      </TableCell>
                      <TableCell>
                        {invoice.projects ? (
                          <div className="flex items-center space-x-1">
                            <span className="hover:text-primary cursor-pointer">{invoice.projects.name}</span>
                            <ExternalLink className="h-3 w-3 text-muted-foreground" />
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {invoice.invoice_items && invoice.invoice_items.length > 0 ? 
                            invoice.invoice_items.map((item, index) => (
                              <span key={index} className="text-xs bg-muted px-2 py-1 rounded">
                                {item.products?.name || 'Unknown Product'}
                              </span>
                            )) : 
                            <span className="text-muted-foreground">—</span>
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">{invoice.po_number || "—"}</span>
                      </TableCell>
                      <TableCell>
                        {invoice.issue_date ? format(new Date(invoice.issue_date), "MMM dd, yyyy") : "—"}
                      </TableCell>
                      <TableCell>
                        {invoice.due_date ? format(new Date(invoice.due_date), "MMM dd, yyyy") : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">
                          {invoice.amount ? 
                            formatCurrency(invoice.amount, invoice.currency || 'EUR') : 
                            "—"}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <InvoiceStatusBadge status={invoice.status as any} />
                          <InvoiceDueBadge dueDate={invoice.due_date} status={invoice.status} paidDate={invoice.paid_date} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {invoice.paid_date ? format(new Date(invoice.paid_date), "MMM dd, yyyy") : "—"}
                      </TableCell>
                      <TableCell>
                        {invoice.pdf_url ? (
                          <Button variant="ghost" size="sm" asChild>
                            <a href={invoice.pdf_url} target="_blank" rel="noopener noreferrer">
                              <FileText className="h-4 w-4" />
                            </a>
                          </Button>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button variant="ghost" size="sm" onClick={() => onEdit?.(invoice)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteInvoice} onOpenChange={() => setDeleteInvoice(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this invoice? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => {
                if (deleteInvoice) {
                  onDelete?.(deleteInvoice);
                  setDeleteInvoice(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Pagination */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-muted-foreground">
            Showing {startItem} to {endItem} of {totalItems} invoices
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onPrevPage}
              disabled={!hasPrevPage}
            >
              Previous
            </Button>
            <span className="text-sm">
              Page {currentPage} of {Math.ceil(totalItems / itemsPerPage)}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={onNextPage}
              disabled={!hasNextPage}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </>
  );
}